/**
 * P3-4 员工绩效页面
 *   - sales：看自己当月 6 维分数
 *   - manager：看团队 + 自己
 *   - admin / director：看全公司排行
 */
import { useState } from 'react';
import { Input, Tabs } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { PageHeader, HomeCard, Pill, TableWrap, StatCard, Avatar } from '../components/ui';

export function StaffPerformancePage() {
  const user = useAuthStore((s) => s.user)!;
  const isMgmt = ['manager', 'director', 'admin'].includes(user.role);
  const isAdmin = ['director', 'admin'].includes(user.role);
  const [tab, setTab] = useState(isMgmt ? 'team' : 'me');

  const items: any[] = [{ key: 'me', label: '我的绩效', children: <MyPerformance /> }];
  if (isMgmt) items.push({ key: 'team', label: '团队绩效', children: <TeamPerformance /> });
  if (isAdmin) items.push({ key: 'ranking', label: '全公司排行', children: <Ranking /> });

  return (
    <>
      <PageHeader
        breadcrumb={['绩效']}
        title="📈 员工绩效"
        subtitle="6 维加权综合评分 · 每月 1 号 02:00 自动结算上月数据"
      />
      <div className="work-content">
        <Tabs activeKey={tab} onChange={setTab} items={items} />
      </div>
    </>
  );
}

function MyPerformance() {
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const { data: p } = useQuery<any>({
    queryKey: ['perf-me', month],
    queryFn: () => $.get(`/staff-performance/me?month=${month}`),
  });

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Input value={month} onChange={(e) => setMonth(e.target.value)}
               placeholder="YYYY-MM" style={{ width: 160 }} />
      </div>
      {!p && (
        <HomeCard title="当月暂无数据">
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>
            本月绩效会在下个月 1 号 02:00 自动计算。<br />
            admin 可去测试中心点"重算月度员工绩效"立即生成。
          </div>
        </HomeCard>
      )}
      {p && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
            <StatCard label="综合分" value={Number(p.compositeScore).toFixed(1)}
                      unit="/100" valueColor="primary" />
            <StatCard label="团队排名" value={p.rankInTeam ?? '—'} unit="" valueColor="success" />
            <StatCard label="公司排名" value={p.rankInCompany ?? '—'} unit="" valueColor="success" />
            <StatCard label="跟进数" value={p.followupCount} unit="条" />
          </div>
          <HomeCard title="6 维明细">
            <div style={{ padding: 16 }}>
              <DimRow label="跟进数（10%）" v={p.followupCount} max={100}
                      hint={`本月跟进 ${p.followupCount} 条，100 条为满分`} />
              <DimRow label="平均质量分（25%）" v={Number(p.avgFollowupQuality)} max={100} fmt="num"
                      hint="AI 给每条跟进打 0-100 分的均值" />
              <DimRow label="客户推进率（20%）" v={Number(p.customerProgressionRate) * 100} max={50}
                      hint="本月有阶段推进的客户 / 持客数 · 50% 为满分" fmt="pct" />
              <DimRow label="成单金额（20%）" v={Number(p.dealAmount)} max={100000}
                      hint={`本月成 ${p.dealCount} 单 · ¥${Number(p.dealAmount).toLocaleString()}`} fmt="rmb" />
              <DimRow label="长周期照顾率（10%）" v={Number(p.longCycleCareRate) * 100} max={100}
                      hint="本月触达过的长周期客户 / 长周期持客数" fmt="pct" />
              <DimRow label="日配额完成率（15%）" v={Number(p.quotaCompletionRate) * 100} max={100}
                      hint="本月达标天数 / 总天数" fmt="pct" />
            </div>
          </HomeCard>
        </>
      )}
    </>
  );
}

function DimRow({ label, v, max, hint, fmt }: { label: string; v: number; max: number; hint?: string; fmt?: 'num' | 'pct' | 'rmb' }) {
  const pct = Math.min(100, max > 0 ? (v / max) * 100 : 0);
  const display = fmt === 'pct' ? `${v.toFixed(1)}%`
                : fmt === 'rmb' ? `¥${v.toLocaleString()}`
                : fmt === 'num' ? v.toFixed(1)
                : String(v);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-2)' }}>{display}</span>
      </div>
      <div style={{
        width: '100%', height: 6, background: 'var(--bg-2)', borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: pct >= 70 ? 'var(--success)' : pct >= 40 ? 'var(--warning)' : 'var(--danger)',
        }} />
      </div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function TeamPerformance() {
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const { data: rows } = useQuery<any[]>({
    queryKey: ['perf-team', month],
    queryFn: () => $.get(`/staff-performance/team?month=${month}`),
  });
  return <PerfTable month={month} setMonth={setMonth} rows={rows ?? []} title="团队绩效" />;
}

function Ranking() {
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const { data: rows } = useQuery<any[]>({
    queryKey: ['perf-ranking', month],
    queryFn: () => $.get(`/staff-performance/ranking?month=${month}`),
  });
  return <PerfTable month={month} setMonth={setMonth} rows={rows ?? []} title="全公司排行（前 50）" />;
}

function PerfTable({ month, setMonth, rows, title }: { month: string; setMonth: (s: string) => void; rows: any[]; title: string }) {
  const { data: usersResp } = useQuery<any>({
    queryKey: ['perf-users'],
    queryFn: () => $.get('/users?pageSize=200'),
  });
  const userMap = new Map<number, { realName: string; avatarUrl: string | null }>();
  (usersResp?.items ?? usersResp ?? []).forEach((u: any) =>
    userMap.set(Number(u.id), { realName: u.realName, avatarUrl: u.avatarUrl ?? null }),
  );

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Input value={month} onChange={(e) => setMonth(e.target.value)}
               placeholder="YYYY-MM" style={{ width: 160 }} />
      </div>
      <HomeCard title={`${title} · ${month}`}>
        <TableWrap>
          <table className="ck-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>团队排名</th>
                <th>公司排名</th>
                <th>姓名</th>
                <th>综合分</th>
                <th>跟进数</th>
                <th>平均质量</th>
                <th>推进率</th>
                <th>成单 ¥</th>
                <th>配额达标</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>
                  暂无数据
                </td></tr>
              )}
              {rows.map((r: any) => {
                const u = userMap.get(Number(r.userId));
                return (
                <tr key={r.id}>
                  <td>{r.rankInTeam ?? '—'}</td>
                  <td>{r.rankInCompany ?? '—'}</td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Avatar userId={r.userId} src={u?.avatarUrl ?? null}
                              name={u?.realName ?? `#${r.userId}`} size="sm" />
                      <span>{u?.realName ?? `#${r.userId}`}</span>
                    </span>
                  </td>
                  <td><Pill tone={Number(r.compositeScore) >= 70 ? 'success' : Number(r.compositeScore) >= 40 ? 'warning' : 'danger'}>
                    {Number(r.compositeScore).toFixed(1)}
                  </Pill></td>
                  <td>{r.followupCount}</td>
                  <td>{Number(r.avgFollowupQuality).toFixed(1)}</td>
                  <td>{(Number(r.customerProgressionRate) * 100).toFixed(1)}%</td>
                  <td>¥{Number(r.dealAmount).toLocaleString()}</td>
                  <td>{(Number(r.quotaCompletionRate) * 100).toFixed(1)}%</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      </HomeCard>
    </>
  );
}
